import React from 'react';

import { renderChild } from '@/shared/react-rendering';

type LiveDataProps<T> = {
  isLoading?: boolean;
  loadingState?: React.ReactNode;
  fallback?: React.ReactNode;
  data?: T;
  children: React.ReactNode | ((data: T) => React.ReactNode);
};

/**
 * A control component for rendering live data from the network, which may be in
 * a loading, error, or resolved state.
 *
 * @param isLoading Whether the data is currently loading.
 * @param loadingState The React component to render while data is loading.
 * @param fallback The React component to render if data is not available.
 * @param data The data being loaded from the network.
 * @param children The children to render once the data is resolved. This can be
 * either a static React component or a function that accepts the resolved data
 * and returns a React component.
 */
export function LiveData<T>({ isLoading = false, loadingState, fallback, data, children }: LiveDataProps<T>) {
  if (isLoading) {
    return loadingState;
  }

  if (!data) {
    return fallback ? fallback : null;
  }

  return renderChild(children, data);
}
